/**
 * Probe the runtime environment for available tools and runtimes, and build
 * a markdown section describing them for the system prompt.
 *
 * Runs once at session start. All probes execute in parallel with a per-probe
 * timeout so the total wall time is bounded by the slowest single probe.
 */

import { execFile } from "node:child_process";
import { promisify } from "node:util";

const execFileAsync = promisify(execFile);

const PROBE_TIMEOUT_MS = 1500;

interface VersionProbe {
  label: string;
  bin: string;
  args: string[];
  /** Extract version from stdout (first line by default). */
  extract?: (output: string) => string;
}

const VERSION_PROBES: VersionProbe[] = [
  { label: "Python", bin: "python3", args: ["--version"] },
  { label: "Node.js", bin: "node", args: ["--version"] },
  { label: "Bun", bin: "bun", args: ["--version"] },
  { label: "Git", bin: "git", args: ["--version"] },
  { label: "npm", bin: "npm", args: ["--version"] },
  {
    label: "pip",
    bin: "pip",
    args: ["--version"],
    extract: (out) => out.split(" ").slice(0, 2).join(" "),
  },
];

const PRESENCE_PROBES = ["curl", "jq", "ssh", "wget", "rg"];

async function runProbe(bin: string, args: string[]): Promise<string | null> {
  try {
    const { stdout } = await execFileAsync(bin, args, { timeout: PROBE_TIMEOUT_MS });
    return stdout.trim().split("\n")[0] ?? null;
  } catch {
    return null;
  }
}

async function checkPresence(bin: string): Promise<boolean> {
  try {
    await execFileAsync("which", [bin], { timeout: PROBE_TIMEOUT_MS });
    return true;
  } catch {
    return false;
  }
}

/**
 * Probe the runtime environment for available tools and runtimes at
 * session start, and build a markdown section describing them for the
 * system prompt.
 *
 * All probes execute in parallel with a per-probe timeout so total wall
 * time is bounded by the slowest single probe.
 *
 * @param projectDir - The project working directory
 * @returns Markdown section string, or undefined when no tools are found
 * @docLink packages/runner/dev-guide#environment-variables
 */
export async function buildEnvironmentSection(projectDir: string): Promise<string | undefined> {
  const versionResults = await Promise.allSettled(
    VERSION_PROBES.map(async (probe) => {
      const raw = await runProbe(probe.bin, probe.args);
      if (!raw) return null;
      const version = probe.extract ? probe.extract(raw) : raw;
      return { label: probe.label, version };
    }),
  );

  const presenceResults = await Promise.allSettled(PRESENCE_PROBES.map(checkPresence));

  const runtimes: string[] = [];
  for (const result of versionResults) {
    if (result.status === "fulfilled" && result.value) {
      runtimes.push(`${result.value.label} (${result.value.version})`);
    }
  }

  const tools: string[] = [];
  for (let i = 0; i < PRESENCE_PROBES.length; i++) {
    const result = presenceResults[i];
    if (result?.status === "fulfilled" && result.value) {
      tools.push(PRESENCE_PROBES[i]!);
    }
  }

  if (runtimes.length === 0 && tools.length === 0) return undefined;

  const lines: string[] = [
    "## Environment",
    "",
    "You are running inside an isolated container with the following tools available:",
    "",
  ];

  if (runtimes.length > 0) {
    lines.push("**Runtimes:**");
    for (const r of runtimes) lines.push(`- ${r}`);
    lines.push("");
  }

  if (tools.length > 0) {
    lines.push(`**Tools:** ${tools.join(", ")}`);
    lines.push("");
  }

  const shell = process.env.SHELL ?? "sh";
  lines.push(`**Shell:** ${shell}`);
  lines.push(`**Working directory:** ${projectDir}`);

  return lines.join("\n");
}
